import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IItemState, IPreviousRequestWithProcedure } from '../episodes/Episode';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ProcessScrollItem } from '../../../../common/enums/processScrollItem';
import { ServiceType } from '../../../../common/enums/serviceType';
import { UrlBuilder } from '../../../../common/utils/urlBuilder';
import { fetchJson } from '../../../store/fetch';
import { Loading } from '../../elements/common';
import { Component } from '../../elements/Component';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { ReloadEpisode } from '../episodes/reloadEpisode';
import { RequestItemAddEdit } from './RequestItemAddEdit';

interface IRequestItemProps {
    addProcedure?: () => Promise<void>;
    addReport?: (reportCategory: ReportCategories) => Promise<void>;
    disableFormAutosave: boolean;
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    itemState: IItemState;
    onLoaded: () => void;
    onReloadPatient?: () => Promise<void>;
    process: IProcess;
    reloadProcess: (reload?: ReloadEpisode) => Promise<void>;
    removeProcess: (requestId?: string) => Promise<void>;
    scrollToNewProcess?: () => void;
    scrollToProcessItem: (requestId: string, scrollItem: ProcessScrollItem) => Promise<void>;
    setHideCreateActions: (values: boolean) => Promise<void>;
    updateItemState: (itemState: IItemState) => Promise<void>;
    updateProcessItemStateByRequestId?: (
        requestId: string,
        itemState: IItemState,
        scrollItem: ProcessScrollItem
    ) => Promise<void>;
}

interface IRequestItemState {
    previousRequest?: IPreviousRequestWithProcedure;
    request?: IRequestGetOne;
}

export class RequestItem extends Component<IRequestItemProps, IRequestItemState> {
    public async componentDidMount() {
        await this.loadRequest();

        this.props.onLoaded();
    }

    public async componentDidUpdate(prevProps: Readonly<IRequestItemProps>) {
        if (
            prevProps.process.status !== this.props.process.status ||
            (prevProps.process.procedure === undefined && this.props.process.procedure !== undefined) ||
            prevProps.process.diagnosticReports.length < this.props.process.diagnosticReports.length ||
            this.props.process.serviceId !== prevProps.process.serviceId ||
            prevProps.process.recipientPractitionerId !== this.props.process.recipientPractitionerId ||
            prevProps.process.requesterPractitionerId !== this.props.process.requesterPractitionerId
        ) {
            await this.loadRequest();
        }
    }

    public render() {
        return (
            <Loading
                item={this.state.request}
                loaded={(request) => (
                    <>
                        {this.props.itemState.mode === 'read' && this.renderCustomControl(request)}
                        {this.props.itemState.mode === 'edit' && (
                            <RequestItemAddEdit
                                disableFormAutosave={this.props.disableFormAutosave}
                                formId={this.props.formId}
                                isFirstItem={this.props.isFirstItem}
                                isLastItem={this.props.isLastItem}
                                itemState={this.props.itemState}
                                onCancel={async () => await this.changeMode('read')}
                                onReloadPatient={this.props.onReloadPatient}
                                onSave={async () => {
                                    await this.loadRequest();
                                    await this.props.reloadProcess(ReloadEpisode.reloadSelf);
                                    await this.changeMode('read');
                                }}
                                previousRequest={this.state.previousRequest}
                                process={this.props.process}
                                reloadProcess={async (reload?: ReloadEpisode) => {
                                    if (reload === ReloadEpisode.reloadAll) {
                                        await this.props.reloadProcess(reload);
                                    }
                                }}
                                removeProcess={this.props.removeProcess}
                                request={request}
                                scrollToNewProcess={this.props.scrollToNewProcess}
                                serviceId={request.serviceId}
                            />
                        )}
                    </>
                )}
            />
        );
    }

    private renderCustomControl(request: IRequestGetOne) {
        const CustomControl = loadCustomControl(
            request.customControlVersion,
            CustomControlEntity.RequestRead,
            request.service.code,
            request.service.serviceType
        );

        return (
            <CustomControl
                addProcedure={this.props.addProcedure}
                addReport={this.props.addReport}
                assignProcessColleague={async () => {
                    await this.props.reloadProcess();
                    await this.changeMode('read');
                }}
                editRequest={async () => await this.changeMode('edit')}
                isFirstItem={this.props.isFirstItem}
                isLastItem={this.props.isLastItem}
                itemState={this.props.itemState}
                previousRequest={this.state.previousRequest}
                process={this.props.process}
                reloadProcess={this.props.reloadProcess}
                reloadRequest={async () => {
                    await this.loadRequest();
                    await this.props.reloadProcess();
                }}
                removeProcess={this.props.removeProcess}
                request={request}
                serviceCode={request.service.code}
                setHideCreateActions={this.props.setHideCreateActions}
                updateItemState={this.props.updateItemState}
                updateProcessItemStateByRequestId={this.props.updateProcessItemStateByRequestId}
            />
        );
    }

    private async changeMode(mode: 'edit' | 'read') {
        await this.props.updateItemState({ ...this.props.itemState, mode: mode });

        if (mode === 'edit') {
            await this.props.scrollToProcessItem(this.props.process.id, ProcessScrollItem.request);
        }
    }

    private async loadRequest() {
        const urlBuilder = new UrlBuilder(`/api/requests/${this.props.process.id}`);
        const itemAuthorizations: (keyof Required<IRequestGetOne>['authorization'])[] = [
            'canAddConsultDiagnosticReport',
            'canAddIntakeDiagnosticReport',
            'canAddDischargeDiagnosticReport',
            'canUpdateRequestToAccepted',
            'canUpdateRequestToRejected',
            'canUpdateRejectedRequestToResent',
            'canUpdateRejectedRequestToArchived',
            'canUpdateRequestToCancelled',
            'canTakeProcess',
            'canTakeProcessFromColleague',
            'canAddProcedure',
            'canUpdateRequest',
            'canBecomeRequester',
            'canAssignToColleague',
            'canSendMessage',
            'canAddReferral',
            'canResendInvitations',
            'canGetAccessToCounseling',
            'hasAccessToCounseling',
            'canAddAddendum',
            'canAddExaminationDiagnosticReport',
        ];

        urlBuilder.add('showAssets', true).add('includeItemAuthorizations', itemAuthorizations);

        let result = await fetchJson(urlBuilder.build(), 'GET');
        const request = result.json;

        let previousRequest;
        if (
            request &&
            request.previousRequestId &&
            request.serviceType !== ServiceType.referral &&
            request.serviceType !== ServiceType.referralSmall
        ) {
            result = await fetchJson(`/api/requests/${request.previousRequestId}`, 'GET');
            if (result.json.serviceType === ServiceType.examination) {
                const resultProcedure = await fetchJson(`/api/requests/${request.previousRequestId}/procedure`, 'GET');
                result.json.procedure = resultProcedure.json;
            }
            previousRequest = result.json;
        }

        await this.setState({ previousRequest, request });
    }
}
